<script setup lang="ts">
import { useDebounce } from '@vueuse/core'
import { useSearchResultsRandomizer } from '~/composables/useSearchResultsRandomizer'
import { LucideSearch, LucideNewspaper, LucideCircleX } from 'lucide-vue-next'
import { FeaturePagination } from '~/components/feature/pagination'
import type { SearchResponse } from 'meilisearch'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import type { ICardsQueryParams } from '~/components/entity/card/item'
import type { TBankItemCard } from '~/components/entity/bank/item'
import { EntityBankItem, EntityBankItemSkeleton } from '~/components/entity/bank'
import { banksListFilter } from '~/components/widget/banks-page/list-meili-search/mocks'

const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()
const { locale } = useI18n()

// Track search query state for banks
const searchQueryState = useState<string>('searchQuery_banks', () => '')

const props = withDefaults(
  defineProps<{
    queryParams?: ICardsQueryParams
  }>(),
  {
    queryParams: () => ({
      per_page: '10',
      q: ''
    })
  }
)

const hitsPerPage = Number(route.query.per_page || props.queryParams.per_page || 10)
const currentPage = ref(Number(route.query.page) || 1)

const searchQueryString = ref(String(route.query.q || ''))
const debouncedQuery = useDebounce(searchQueryString, 500)
const selectedFilters = ref<string[]>([])

// Watch for search query state changes
watch(
  () => searchQueryState.value,
  (newValue) => {
    // Apply value only if it's not empty, has changed, and differs from current search value
    if (newValue && newValue !== searchQueryString.value) {
      searchQueryString.value = newValue
      // Clear state after applying
      searchQueryState.value = ''
    }
  },
  { immediate: true }
)

const activeFilter = computed(() => {
  if (!selectedFilters.value.length) return undefined

  const filters = selectedFilters.value.map((o) => `${o} > 0`)
  return [`(${filters.join(' OR ')})`]
})

const searchKey = computed(
  () =>
    `banks-search-${debouncedQuery.value}-${currentPage.value}-${selectedFilters.value.join(',')}`
)

const {
  data: result,
  pending: isSearching,
  error: searchError,
  refresh
} = await useAsyncData<SearchResponse<TBankItemCard> | null>(
  searchKey,
  async () => {
    try {
      const { search } = useMeiliIndexSearch(`${config.public.meiliEnvironment}_banks`)

      return await search(debouncedQuery.value, {
        hitsPerPage,
        page: currentPage.value,
        sort: ['weight:desc', 'highlight:desc', 'updated_at:desc'],
        attributesToSearchOn: [
          'title.ky',
          'title.ru',
          'official_title.ky',
          'official_title.ru',
          'description.ru',
          'description.ky',
          'slug',
          'swift'
        ],
        attributesToHighlight: ['title'],
        ...(activeFilter.value ? { filter: activeFilter.value } : {})
      })
    } catch (e) {
      console.error('Fetch error:', e)
      return null
    }
  },
  {
    server: true,
    client: true,
    default: () => null
  }
)

const hasError = computed(() => !!searchError.value)

// Redirect to /banks if page param is set but no results found (e.g. bot crawling ?page=9)
if (import.meta.server && currentPage.value > 1 && result.value?.hits?.length === 0) {
  await navigateTo('/banks', { redirectCode: 301 })
}

const { shuffleHighlightedAfterMount } = useSearchResultsRandomizer()
const orderedHits = shuffleHighlightedAfterMount(() => result.value?.hits || [])

const typedHits = computed(() => {
  return orderedHits.value.map((hit) => ({
    id: hit.id,
    logo: hit.logo,
    title: hit._formatted?.title[locale.value] || hit.title[locale.value],
    url: hit.url,
    slug: hit.slug,
    license: hit.license,
    branches_count: hit.branches_count,
    atms_count: hit.atms_count,
    terminals_count: hit.terminals_count,
    deposits_count: hit.deposits_count,
    credits_count: hit.credits_count,
    cards_count: hit.cards_count,
    vacancies_count: hit.vacancies_count,
    highlight: hit.highlight,
    ratings: hit.ratings ? hit.ratings.average_rate : null
  }))
})

// Update query parameters in the URL
const updateQueryParams = () => {
  router.replace({
    path: route.path,
    query: {
      ...route.query,
      q: debouncedQuery.value || undefined,
      page: currentPage.value > 1 ? String(currentPage.value) : undefined
    }
  })
}

// Watch for changes and refresh data
watch(currentPage, async () => {
  updateQueryParams()
  await refresh()
})

watch(debouncedQuery, () => {
  updateQueryParams()
})

const changePage = (page: number) => {
  currentPage.value = page
}

const updateSelectedFilters = (newSelected: string[]) => {
  selectedFilters.value = newSelected
}

const clearSearch = () => {
  searchQueryString.value = ''
  searchQueryState.value = ''
}
</script>

<template>
  <div class="space-y-5">
    <div class="relative">
      <Input
        v-model="searchQueryString"
        :placeholder="$t('common.search_placeholder')"
        class="h-[52px] rounded-lg pl-14 pr-14"
      />
      <LucideSearch class="absolute left-4 top-3.5 size-6 text-gray-600" />
      <Button
        v-if="searchQueryString"
        size="icon"
        variant="link"
        class="absolute right-4 top-3.5 size-6 text-red-400 hover:text-red-400"
        @click="clearSearch"
      >
        <LucideCircleX />
      </Button>
    </div>

    <div class="flex w-full gap-2">
      <div v-for="type in banksListFilter" :key="type.type" class="inline-flex items-center">
        <input
          :id="type.type"
          :checked="selectedFilters.some((f) => f === type.type)"
          type="checkbox"
          :value="type.type"
          class="peer hidden"
          @change="
            updateSelectedFilters(
              selectedFilters.some((f) => f === type.type)
                ? selectedFilters.filter((f) => f !== type.type)
                : [...selectedFilters, type.type]
            )
          "
        />
        <label
          :for="type.type"
          class="FilterLabel bg-white px-4 py-2.5 text-sm"
        >
          {{ $t(type.title) }}
        </label>
      </div>
    </div>

    <section v-if="!isSearching && !hasError && result" aria-labelledby="banks">
      <div v-if="result.hits?.length" class="CardsList">
        <Heading id="banks" tag="h2" class="sr-only">{{
          $t('common.banks_of_kyrgyzstan')
        }}</Heading>
        <EntityBankItem
          v-for="item in typedHits"
          :key="item.id"
          :item="item"
          :hide-organization-details="false"
        />

        <div class="mt-7 flex justify-end">
          <FeaturePagination
            :meta="{
              current_page: result.page || 1,
              total: result.totalHits || 0,
              per_page: result.hitsPerPage || 0,
              last_page: result.totalPages || 0
            }"
            :links="{
              prev:
                currentPage > 1
                  ? `?q=${searchQueryString}&page=${result.page ? result.page - 1 : 1}`
                  : null,
              next:
                currentPage < (result.totalPages || 1)
                  ? `?q=${searchQueryString}&page=${result.page ? result.page + 1 : 1}`
                  : null
            }"
            @change="changePage"
          />
        </div>
      </div>

      <Alert v-else variant="blue">
        <LucideNewspaper />
        <AlertDescription>{{ $t('common.nothing_found') }}</AlertDescription>
      </Alert>
    </section>

    <section v-else-if="isSearching">
      <EntityBankItemSkeleton :count="hitsPerPage" :hide-organization-details="false" />
    </section>

    <section v-else-if="hasError">
      <Alert variant="yellow">
        <AlertTitle>{{ $t('common.error_loading_title') }}</AlertTitle>
        <AlertDescription>{{ $t('common.error_loading_description') }}</AlertDescription>
      </Alert>
    </section>
  </div>
</template>
